// AGC: i18n Configuration for DroidFarm
// AGC: Uses i18next with react-i18next for multi-language support
// AGC: Supports English, Portuguese, and Spanish

import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { doc, setDoc, getDoc } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { getAuth } from 'firebase/auth'

// AGC: Import default locale files (fallback when Firestore is unavailable)
import en from './locales/en.json'
import pt from './locales/pt.json'
import es from './locales/es.json'

// AGC: Define available languages
export const LANGUAGES = {
  en: { code: 'en', name: 'English', flag: '🇺🇸' },
  pt: { code: 'pt', name: 'Português', flag: '🇧🇷' },
  es: { code: 'es', name: 'Español', flag: '🇪🇸' },
} as const

export type LanguageCode = keyof typeof LANGUAGES

// AGC: Define available currencies with defaults per language
export const CURRENCIES = {
  BRL: { code: 'BRL', symbol: 'R$', name: 'Real Brasileiro', flag: '🇧🇷', locale: 'pt-BR' },
  USD: { code: 'USD', symbol: '$', name: 'US Dollar', flag: '🇺🇸', locale: 'en-US' },
  ARS: { code: 'ARS', symbol: '$', name: 'Peso Argentino', flag: '🇦🇷', locale: 'es-AR' },
  EUR: { code: 'EUR', symbol: '€', name: 'Euro', flag: '🇪🇺', locale: 'de-DE' },
} as const

export type CurrencyCode = keyof typeof CURRENCIES

// AGC: Default currency for each language
export const DEFAULT_CURRENCY: Record<LanguageCode, CurrencyCode> = {
  pt: 'BRL',
  en: 'USD',
  es: 'ARS',
}

// AGC: Define namespaces for organizing translations
export const NAMESPACES = [
  'common',    // AGC: Common UI elements (buttons, labels, etc.)
  'admin',     // AGC: Admin panel translations
  'auth',      // AGC: Authentication messages
  'fields',    // AGC: Field/plot management
  'tasks',     // AGC: Task management
  'weather',   // AGC: Weather information
  'financial', // AGC: Financial/expenses
  'profile',   // AGC: User profile
  'notifications', // AGC: Notifications
] as const

export type Namespace = typeof NAMESPACES[number]

// AGC: Default resources (bundled with app)
const resources = {
  en: { translation: en },
  pt: { translation: pt },
  es: { translation: es },
}

// AGC: Track missing keys for reporting (development only)
const missingKeys = new Set<string>()

/**
 * AGC: Get all missing keys encountered during the session (dev only)
 */
export const getMissingKeys = (): string[] => Array.from(missingKeys)

/**
 * AGC: Clear missing keys set (dev only)
 */
export const clearMissingKeys = (): void => missingKeys.clear()

/**
 * AGC: [LANG_PERSIST] Resolve the initial UI language at startup.
 * AGC: i18next's `lng` option OVERRIDES the LanguageDetector — a hardcoded `lng: 'pt'` therefore
 * AGC: discarded the user's saved choice on every reload. Read the persisted value (same key the
 * AGC: detector + changeLanguage write) and fall back to 'pt' only when nothing is stored
 * AGC: (fresh install / WebView). navigator/htmlTag are deliberately NOT consulted so a fresh
 * AGC: install never lands on the WebView's 'en-US'.
 */
const getInitialLanguage = (): LanguageCode => {
  try {
    const stored = localStorage.getItem('droidfarm_language')
    if (stored && stored in LANGUAGES) return stored as LanguageCode
  } catch {
    // AGC: localStorage can throw in restricted/privacy modes — fall through to the pt default.
  }
  return 'pt'
}

/**
 * AGC: Initialize i18next
 * AGC: Features:
 * AGC: - Auto-detects user's preferred language (browser, localStorage)
 * AGC: - Falls back to English if language not available
 * AGC: - Supports interpolation and pluralization
 * AGC: - Logs missing keys in development for easy debugging
 */
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // AGC: Default resources bundled with the app
    resources,
    
    // AGC: Fallback language when translation is missing — Portuguese is the primary target
    // AGC: audience; English/Spanish fall back to pt for any missing key (better than a random key)
    fallbackLng: 'pt',
    
    // AGC: Default namespace
    defaultNS: 'translation',
    
    // AGC: Enable debug in development (but not too verbose)
    debug: false, // AGC: Set to false to reduce console noise; use missingKeyHandler instead
    
    // AGC: Missing key handling - returns key as fallback but logs in dev
    returnNull: false,
    returnEmptyString: false,
    
    // AGC: Save missing keys for reporting (dev only)
    saveMissing: import.meta.env.DEV,
    
    // AGC: Missing key handler - logs and tracks missing keys
    missingKeyHandler: (lngs, ns, key, fallbackValue) => {
      if (import.meta.env.DEV) {
        const fullKey = `${ns}:${key}`
        if (!missingKeys.has(fullKey)) {
          missingKeys.add(fullKey)
          console.warn(`🔑 AGC: Missing translation key: "${key}" (namespace: ${ns}, languages: ${lngs.join(', ')})`)
        }
      }
    },
    
    // AGC: Interpolation options
    interpolation: {
      // AGC: React already escapes values
      escapeValue: false,
    },
    
    // AGC: Language detection configuration
    detection: {
      // AGC: Order of language detection methods.
      // AGC: Removed 'navigator' and 'htmlTag' — on Android WebView these often return 'en-US',
      // AGC: which was making fresh installs launch in English. We now only honour the user's
      // AGC: explicit choice (stored in localStorage); everything else defaults to pt (see lng).
      order: ['localStorage'],

      // AGC: Cache user's language preference
      caches: ['localStorage'],

      // AGC: localStorage key for language preference
      lookupLocalStorage: 'droidfarm_language',
    },

    // AGC: [LANG_PERSIST] Seed from the persisted choice (getInitialLanguage), defaulting to 'pt'
    // AGC: only when nothing is stored. A literal `lng: 'pt'` here overrode the saved language on
    // AGC: every reload because i18next's `lng` takes precedence over the localStorage detector.
    lng: getInitialLanguage(),
    
    // AGC: React-specific options
    react: {
      // AGC: Wait for translations to load
      useSuspense: false,
    },
  })

/**
 * AGC: Load translations from external source (Firestore)
 * AGC: Called by TranslationContext to sync with database
 * @param language - Language code (en, pt, es)
 * @param translations - Translation object from Firestore
 */
export const loadTranslations = (
  language: LanguageCode,
  translations: Record<string, string>
): void => {
  // AGC: Add or update translations for the specified language
  i18n.addResourceBundle(language, 'translation', translations, true, true)
  console.log(`✅ AGC: Loaded translations for ${language}`)
}

/**
 * AGC: Get current language
 */
export const getCurrentLanguage = (): LanguageCode => {
  // AGC: Default to Portuguese when no language is detected (primary target audience).
  // AGC: Later this will be replaced by an Android-system language detector, but for now
  // AGC: we hardcode pt so fresh installs and WebView fallbacks show Portuguese.
  return (i18n.language?.split('-')[0] as LanguageCode) || 'pt'
}

/**
 * AGC: Change current language
 * @param language - Language code to switch to
 */
export const changeLanguage = async (language: LanguageCode): Promise<void> => {
  await i18n.changeLanguage(language)
  localStorage.setItem('droidfarm_language', language)
  // AGC: Sync to Firestore for cross-device persistence
  const uid = getAuth()?.currentUser?.uid
  if (uid) setDoc(doc(db, 'users', uid), { language }, { merge: true }).catch(() => {})
}

/**
 * AGC: Get all available languages
 */
export const getAvailableLanguages = () => {
  return Object.values(LANGUAGES)
}

/**
 * AGC: Get all available currencies
 */
export const getAvailableCurrencies = () => {
  return Object.values(CURRENCIES)
}

/**
 * AGC: Get current currency from localStorage or default based on language
 */
export const getCurrentCurrency = (): CurrencyCode => {
  const saved = localStorage.getItem('droidfarm_currency')
  if (saved && saved in CURRENCIES) {
    return saved as CurrencyCode
  }
  // AGC: Return default currency for current language
  const lang = getCurrentLanguage()
  return DEFAULT_CURRENCY[lang]
}

/**
 * AGC: Set current currency
 */
export const setCurrency = (currency: CurrencyCode): void => {
  localStorage.setItem('droidfarm_currency', currency)
  // AGC: Sync to Firestore for cross-device persistence
  const uid = getAuth()?.currentUser?.uid
  if (uid) setDoc(doc(db, 'users', uid), { currency }, { merge: true }).catch(() => {})
}

/**
 * AGC: Get currency info
 */
export const getCurrencyInfo = (code: CurrencyCode) => {
  return CURRENCIES[code]
}

/**
 * AGC: Restore language + currency from Firestore when localStorage is empty (new device/cache clear)
 */
export const syncPreferencesFromFirestore = async (): Promise<void> => {
  const uid = getAuth()?.currentUser?.uid
  if (!uid) return
  try {
    const snap = await getDoc(doc(db, 'users', uid))
    const data = snap.data()
    if (data?.language && !localStorage.getItem('droidfarm_language')) {
      await i18n.changeLanguage(data.language)
      localStorage.setItem('droidfarm_language', data.language)
    }
    if (data?.currency && !localStorage.getItem('droidfarm_currency')) {
      localStorage.setItem('droidfarm_currency', data.currency)
    }
  } catch {}
}

export default i18n